import { AfterViewInit, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subscription, combineLatest, debounceTime, distinctUntilChanged, map, merge, take } from 'rxjs';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { selectHiddenFieldsForModule } from 'src/app/core/store/field-visibility/field-visibility.selectors';
import { DATE_FORMAT, DATE_TIME_FORMAT, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/app/config/global.config';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { ICompanyFilter, ICompanyFilterRequest, ICustomer, ILocation } from '../../models/customers.models';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { MatDialog } from '@angular/material/dialog';
import { SetCurrentElementObject } from 'src/app/shared/store/shared.actions';
import {
  DeleteCustomer,
  DeleteCustomerError,
  DeleteCustomerSuccess,
  exportCompanies,
  FetchCustomers,
  RestoreCustomer,
  RestoreCustomerSuccess,
  SetCompanyFilter,
} from '../../store/customers.actions';

/** Soft-delete list filter: active | deleted | all. */
export type CustomerDeletedFilter = 'false' | 'true' | 'all';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';

import { encodeFilterObject, parseFilterRequest, refillCompanyFilter, refillFilter } from 'src/app/shared/utils/helpers';

import { CustomersFiltersComponent } from 'src/app/features/customers/components/customers-filters/customers-filters.component';
import { ITableColumnSetting, TablePreferencesService } from 'src/app/shared/table-preferences.service';
import { SelectionController } from 'src/app/shared/utils/selection-controller';

type ListColumnKey =
  | 'name' | 'locations' | 'erp_id' | 'customer_groups' | 'catalogs'
  | 'total_orders' | 'total_sales' | 'last_order' | 'last_activity';

interface IListColumnConfig {
  label: string;
  visible: boolean;
}

/**
 * Displays the paginated company list with standard page navigation, advanced filtering,
 * multi-select batch actions, expandable per-company location sub-rows, and sortable columns.
 *
 * Key responsibilities:
 * - Synchronises filter/search state with the NgRx store and URL query params.
 * - Reads the customer list and pagination metadata from the NgRx store.
 * - Triggers page changes by dispatching `FetchCustomers` with the requested page;
 *   the reducer replaces the existing list with the new page.
 * - Registers native drag-scroll handlers outside Angular zone to avoid excessive CD.
 */
@Component({
  selector: 'app-list-customers',
  templateUrl: './list-customers.component.html',
  styleUrls: ['./list-customers.component.scss']
})
export class ListCustomersComponent implements OnInit, OnDestroy, AfterViewInit {

  page: IPageCount = { size: DEFAULT_PAGE_SIZE, number: 1 };
  expandedIndex: number = -1;
  DATE_FORMAT = DATE_FORMAT;
  DATE_TIME_FORMAT = DATE_TIME_FORMAT;

  /** Local mirror for synchronous checkbox helpers. */
  customers: ICustomer[] = [];

  readonly customers$: Observable<ICustomer[]> = this.store.select(
    (state) => state.customers.customers
  );

  /** Main location first; mapped via async pipe to avoid per-CD reallocation. */
  readonly displayCustomers$: Observable<ICustomer[]> = this.customers$.pipe(
    map((customers) => customers.map(c => {
      if (c.locations && c.locations.length > 0) {
        const sorted = [...c.locations].sort((a, b) => (b.is_main ? 1 : 0) - (a.is_main ? 1 : 0));
        return { ...c, locations: sorted };
      }
      return c;
    }))
  );

  pagination$: Observable<IPagination | null> = this.store.select((state) => state.customers.metadata);
  isAppLoading$ = this.store.select((state) => state.shared.isAppLoading);

  filter: ICompanyFilterRequest | undefined = undefined;
  key: string | undefined = undefined;
  unparsedFilter: ICompanyFilter | null = null;
  module: string = 'company';

  deletedFilter: CustomerDeletedFilter = 'false';

  activeSortBy: string = 'id';
  activeSortMode: string = 'desc';
  searchInput = new FormControl('');

  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<ICustomer>((customer) => customer.id!);

  private subscriptions: Subscription[] = [];

  /** Maps a column key to the backend role/field-visibility key (company module). */
  private readonly COLUMN_TO_FIELD_KEY: Partial<Record<ListColumnKey, string>> = {
    locations: 'nbr_locations',
    customer_groups: 'customer_groups',
    total_orders: 'total_orders_count',
  };
  private hiddenCompanyFields = new Set<string>();

  private static readonly THIS_TABLE_KEY = 'companies_list_table';

  /** Anchored to the front: immovable, nothing precedes it, cannot be hidden. */
  private static readonly PINNED_KEYS = new Set<ListColumnKey>(['name']);
  private static readonly NON_HIDEABLE_KEYS = new Set<ListColumnKey>(['name']);

  private static readonly DEFAULT_ORDER: ListColumnKey[] = [
    'name', 'locations', 'erp_id', 'customer_groups', 'catalogs',
    'total_orders', 'total_sales', 'last_order', 'last_activity',
  ];

  private readonly COLUMN_WIDTHS: Partial<Record<ListColumnKey, string>> = {
    name: '22%',
  };

  columns: Record<ListColumnKey, IListColumnConfig> = {
    name:            { label: 'companies.full_name_id', visible: true },
    locations:       { label: 'common.location', visible: true },
    erp_id:          { label: 'companies.erp_id', visible: true },
    customer_groups: { label: 'companies_groups.companies_groups', visible: true },
    catalogs:        { label: 'Catalogs.catalogs', visible: true },
    total_orders:    { label: 'companies.total_orders', visible: true },
    total_sales:     { label: 'companies.total_sales', visible: true },
    last_order:      { label: 'companies.last_order', visible: true },
    last_activity:   { label: 'companies.last_activity', visible: true },
  };

  /** Role-allowed columns passed to the <ui-table-column-settings> popover. */
  pickerColumns: ITableColumnSetting[] = [];
  /** Current column order. */
  columnOrder: ListColumnKey[] = [];
  /** Memoized 1-based grid `order` per visible column. */
  private gridOrderIndex: Partial<Record<ListColumnKey, number>> = {};

  readonly canCreate$ = this.store.select(selectCanDo('company', 'create'));
  readonly canEdit$ = this.store.select(selectCanDo('company', 'edit'));
  readonly canDelete$ = this.store.select(selectCanDo('company', 'delete'));
  readonly canEditOrDelete$ = combineLatest([this.canEdit$, this.canDelete$]).pipe(
    map(([canEdit, canDelete]) => canEdit || canDelete)
  );

  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog,
    private router: Router,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private actions$: Actions,
    private zone: NgZone,
    private tablePrefs: TablePreferencesService
  ) {
    const queryParamsSub = this.activatedRoute.queryParams.subscribe(params => {
      const pageNumber = parseInt(params['page'] || '1');
      const pageCount = PAGE_SIZE_OPTIONS.includes(parseInt(params['count'])) ? parseInt(params['count']) : DEFAULT_PAGE_SIZE;
      this.key = params['keyword'] ?? '';
      this.searchInput.setValue(this.key ?? '');
      if (params['filter']) {
        try {
          this.unparsedFilter = refillFilter(this.module, JSON.parse(decodeURIComponent(params['filter']))) as ICompanyFilter;
          if (this.unparsedFilter)
            this.filter = parseFilterRequest(this.module, this.unparsedFilter);
        } catch (error) {
          this.filter = undefined;
        }
      }
      this.page = { ...this.page, number: pageNumber, size: pageCount };
    });
    this.subscriptions.push(queryParamsSub);
  }

  ngOnInit(): void {
    this.initColumns();

    const customersSub = this.customers$.subscribe((customers) => {
      this.customers = customers;
      this.selection.setRows(customers);
    });
    this.subscriptions.push(customersSub);

    // Restore filter/search from URL on page refresh / direct load so the
    // store subscription below does not overwrite them with empty defaults.
    if (this.unparsedFilter || this.key) {
      this.store.dispatch(SetCompanyFilter({
        keyword: this.key || '',
        advanced: this.unparsedFilter ?? refillCompanyFilter()
      }));
    }

    const sub1 = this.store
      .select((state) => state.customers.companyFilterState)
      .pipe(
        debounceTime(10),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe(filter => {
        this.unparsedFilter = filter.advanced;
        this.filter = parseFilterRequest(this.module, filter.advanced);
        this.key = filter.keyword;
        // Soft-delete filter now lives in the filter dialog (companiesInfoFilter.deleted).
        this.deletedFilter = (filter.advanced?.companiesInfoFilter?.deleted as CustomerDeletedFilter) ?? 'false';
        this.searchInput.setValue(this.key ?? '', { emitEvent: false });
        // Preserve the current page (read from the URL on (re)entry) instead of
        // forcing page 1; the action handlers reset to page 1 on filter/search changes.
        this.toPage(this.page);
      });

    this.subscriptions.push(sub1);

    const sub2 = this.searchInput.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => this.search(value ?? ''));
    this.subscriptions.push(sub2);

    const subHidden = this.store
      .select(selectHiddenFieldsForModule('company'))
      .subscribe((hidden) => {
        this.hiddenCompanyFields = new Set(hidden);
        // Role visibility can arrive after init — rebuild the picker + grid order.
        this.pickerColumns = this.buildPickerColumns(this.columnOrder);
        this.recomputeGridOrder();
      });
    this.subscriptions.push(subHidden);
  }

  isColumnAllowedByRole(key: ListColumnKey): boolean {
    const fieldKey = this.COLUMN_TO_FIELD_KEY[key];
    return !fieldKey || !this.hiddenCompanyFields.has(fieldKey);
  }

  isColumnVisible(key: ListColumnKey): boolean {
    if (!this.columns?.[key]?.visible) return false;
    return this.isColumnAllowedByRole(key);
  }

  /** Visible columns in display order. */
  get orderedColumns(): ListColumnKey[] {
    return this.columnOrder.filter((key) => this.isColumnVisible(key));
  }

  /** CSS `order` for a grid cell (1-based; matches the grid-template order). */
  columnOrderIndex(key: ListColumnKey): number {
    return this.gridOrderIndex[key] ?? 0;
  }

  get gridTemplateColumns(): string {
    // Trailing fixed track for the per-row actions (kebab) column.
    return this.orderedColumns
      .map((key) => this.COLUMN_WIDTHS[key] ?? 'minmax(0, 1fr)')
      .join(' ') + ' 56px';
  }

  /** CSS `order` for the trailing actions column (after every data column). */
  get actionsColumnOrder(): number {
    return this.orderedColumns.length + 1;
  }

  /** Hydrate column visibility + order from stored user preferences. */
  private initColumns(): void {
    const stored = this.tablePrefs.applyPreferences(
      ListCustomersComponent.THIS_TABLE_KEY,
      this.buildDefaultColumnSettings()
    );
    stored.forEach((column) => this.applyVisibility(column));
    this.columnOrder = this.pinnedFirst(stored.map((column) => column.key as ListColumnKey));
    this.pickerColumns = this.buildPickerColumns(this.columnOrder);
    this.recomputeGridOrder();
  }

  /** Emitted by <ui-table-column-settings> on reorder / show / hide. */
  onColumnsChange(columns: ITableColumnSetting[]): void {
    columns.forEach((column) => this.applyVisibility(column));
    this.columnOrder = this.pinnedFirst(columns.map((column) => column.key as ListColumnKey));
    this.pickerColumns = columns;
    this.recomputeGridOrder();
    this.tablePrefs.save(ListCustomersComponent.THIS_TABLE_KEY, columns);
  }

  private applyVisibility(column: ITableColumnSetting): void {
    const target = this.columns[column.key as ListColumnKey];
    if (target) target.visible = column.hideable === false ? true : column.visible;
  }

  private buildDefaultColumnSettings(): ITableColumnSetting[] {
    return ListCustomersComponent.DEFAULT_ORDER.map((key) => this.toColumnSetting(key));
  }

  private buildPickerColumns(order: ListColumnKey[]): ITableColumnSetting[] {
    return order
      .filter((key) => this.isColumnAllowedByRole(key))
      .map((key) => this.toColumnSetting(key));
  }

  private toColumnSetting(key: ListColumnKey): ITableColumnSetting {
    return {
      key,
      label: this.columns[key].label,
      visible: this.columns[key].visible,
      pinned: ListCustomersComponent.PINNED_KEYS.has(key),
      hideable: !ListCustomersComponent.NON_HIDEABLE_KEYS.has(key),
    };
  }

  private pinnedFirst(keys: ListColumnKey[]): ListColumnKey[] {
    const pinned = keys.filter((key) => ListCustomersComponent.PINNED_KEYS.has(key));
    const rest = keys.filter((key) => !ListCustomersComponent.PINNED_KEYS.has(key));
    return [...pinned, ...rest];
  }

  private recomputeGridOrder(): void {
    const index: Partial<Record<ListColumnKey, number>> = {};
    this.orderedColumns.forEach((key, i) => (index[key] = i + 1));
    this.gridOrderIndex = index;
  }

  loadCustomers(): void {
    this.store.dispatch(
      FetchCustomers({
        page: this.page,
        filter: this.filter,
        key: this.key,
        sort_by: this.activeSortBy,
        sort_mode: this.activeSortMode,
        deleted: this.deletedFilter,
      })
    );
  }

  getCompanyCurrencySymbol(customer: ICustomer): string {
    const main = customer.locations?.find(l => l.is_main);
    return main?.currency?.symbol
      || customer.locations?.[0]?.currency?.symbol
      || '€';
  }

  getActivityIcon(activityType: string): string {
    switch (activityType) {
      case 'consult_catalog': return 'visibility';
      case 'request_quotation': return 'shopping_cart';
      case 'place_order': return 'local_mall';
      case 'login': return 'mail';
      default: return 'visibility';
    }
  }

  getActivityLabel(activityType: string): string {
    const key = ['consult_catalog', 'request_quotation', 'place_order', 'login'].includes(activityType)
      ? activityType
      : 'default';
    return this.translateService.instant(`customers.activity_labels.${key}`);
  }

  // --- Checkbox ---

  trackByCustomerId(_index: number, customer: ICustomer): number {
    return customer.id!;
  }

  // --- Search / Filter / Pagination ---

  search(key: string) {
    if (this.key !== key) {
      this.key = key;
      this.page = { ...this.page, number: 1 };
      this.store.dispatch(SetCompanyFilter({ keyword: key, advanced: this.unparsedFilter }));
      let queryParams: any = { page: 1, count: this.page.size };
      queryParams.keyword = key !== '' ? key : null;
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge'
      });
    }
  }

  setFilter() {
    const dialogRef = this.dialog.open(CustomersFiltersComponent, {
      minWidth: '650px',
      width: '90vw',
      height: '80vh',
    });
    dialogRef.afterClosed().subscribe((filterResult: ICompanyFilter) => {
      if (filterResult) {
        const encoded = encodeFilterObject(filterResult, false);
        this.page = { ...this.page, number: 1 };
        this.store.dispatch(SetCompanyFilter({ keyword: this.key || '', advanced: filterResult }));
        this.router.navigate([], {
          queryParams: { page: 1, filter: encoded },
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  hasModuleFilters(): boolean {
    // The status filter isn't part of the parsed `filters` array, so count it
    // explicitly — otherwise selecting deleted/all shows no active-filter
    // indication or clear button.
    return (!!this.filter && this.filter.filters?.length > 0) || this.deletedFilter !== 'false';
  }

  clearFilter() {
    this.page = { ...this.page, number: 1 };
    this.store.dispatch(SetCompanyFilter({ keyword: this.key || '', advanced: refillCompanyFilter() }));
  }

  toPage(page: IPageCount) {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.loadCustomers();
    const queryParams: any = { page: this.page.number, count: this.page.size };
    if (this.key && this.key !== '') queryParams.keyword = this.key;
    queryParams.filter = encodeFilterObject(this.unparsedFilter, false);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge'
    });
  }

  addCustomers() {
    this.router.navigate(['/companies/add'], { queryParamsHandling: 'merge' });
  }

  toggleVariants(id: number) {
    this.expandedIndex = this.expandedIndex === id ? -1 : id;
  }

  openLocations(location: ILocation) {
    let filter: any = {};
    if (this.key && this.key !== '') filter.keyword = this.key;
    filter.filter = encodeFilterObject(this.unparsedFilter, false);
    let queryParams = { page: this.page.number, count: this.page.size, ...filter };
    this.router.navigate(['companies/', location.id], { queryParams });
    this.store.dispatch(SetCurrentElementObject({ currentElementObject: { id: location.id, is_main: location.is_main } }));
  }

  addLocations(customer: ICustomer) {
    const location = customer.locations?.length ? customer.locations[0] : null;
    this.router.navigate([`companies/${location?.id}/add-location/${customer.id}`], {
      queryParamsHandling: 'merge',
      queryParams: { action: 'add', name: customer.name, company_id: customer.company_id }
    });
    this.store.dispatch(SetCurrentElementObject({
      currentElementObject: { id: customer.id, name: customer.name, company_id: customer.company_id }
    }));
  }

  sortBy(key: string, mode: 'asc' | 'desc') {
    if (key === this.activeSortBy && mode === this.activeSortMode) return;
    this.activeSortBy = key;
    this.activeSortMode = mode;
    this.toPage({ ...this.page, number: 1 });
  }

  exportCompanies(): void {
    this.store.dispatch(exportCompanies({ filter: this.filter, key: this.key }));
  }

  delete(customerId: number) {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.archive'),
        text: this.translateService.instant('companies.archive_confirm_message'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        const sub = this.actions$.pipe(ofType(DeleteCustomerSuccess), take(1)).subscribe(() => {
          this.toPage(this.page);
          sub.unsubscribe();
        });
        this.store.dispatch(DeleteCustomer({ id: customerId }));
      }
    });
  }

  /** Reactivate a soft-deleted company. */
  restore(customerId: number) {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: { icon: '../../../assets/icones/cancel_alert.svg', confirm_text: this.translateService.instant('common.restore') },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        const sub = this.actions$.pipe(ofType(RestoreCustomerSuccess), take(1)).subscribe(() => {
          this.toPage(this.page);
          sub.unsubscribe();
        });
        this.store.dispatch(RestoreCustomer({ id: customerId }));
      }
    });
  }

  deleteSelectedCustomers() {
    if (!this.selection.hasSelection) return;
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.archive'),
        text: this.translateService.instant('companies.archive_confirm_message'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        const ids = this.selection.ids;
        const sub = merge(
          this.actions$.pipe(ofType(DeleteCustomerSuccess)),
          this.actions$.pipe(ofType(DeleteCustomerError)),
        )
          .pipe(take(ids.length))
          .subscribe({
          complete: () => {
            this.selection.clear();
            this.toPage({ ...this.page, number: 1 });
          },
          });
        this.subscriptions.push(sub);
        ids.forEach(id => this.store.dispatch(DeleteCustomer({ id })));
      }
    });
  }

  private dragState: {
    active: boolean;
    moved: boolean;
    startX: number;
    startScroll: number;
    target: HTMLElement | null;
  } = { active: false, moved: false, startX: 0, startScroll: 0, target: null };
  private blockNextClick = false;

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousedown', this.onGroupsMouseDown, true);
      document.addEventListener('mousemove', this.onGroupsMouseMove, true);
      document.addEventListener('mouseup', this.onGroupsMouseUp, true);
      document.addEventListener('click', this.onGroupsClickCapture, true);
    });
  }

  private onGroupsMouseDown = (e: MouseEvent): void => {
    if (e.button !== 0) return;
    const cell = (e.target as HTMLElement).closest('.cell-groups') as HTMLElement | null;
    if (!cell) return;
    if (cell.scrollWidth <= cell.clientWidth) return;
    this.dragState = {
      active: true,
      moved: false,
      startX: e.pageX,
      startScroll: cell.scrollLeft,
      target: cell
    };
    cell.classList.add('is-dragging');
  };

  private onGroupsMouseMove = (e: MouseEvent): void => {
    if (!this.dragState.active || !this.dragState.target) return;
    const dx = e.pageX - this.dragState.startX;
    if (Math.abs(dx) > 3) this.dragState.moved = true;
    this.dragState.target.scrollLeft = this.dragState.startScroll - dx;
    e.preventDefault();
  };

  private onGroupsMouseUp = (): void => {
    if (!this.dragState.active) return;
    this.dragState.target?.classList.remove('is-dragging');
    const moved = this.dragState.moved;
    this.dragState = { active: false, moved: false, startX: 0, startScroll: 0, target: null };
    if (moved) {
      this.blockNextClick = true;
      setTimeout(() => (this.blockNextClick = false), 0);
    }
  };

  private onGroupsClickCapture = (e: MouseEvent): void => {
    if (!this.blockNextClick) return;
    const cell = (e.target as HTMLElement).closest('.cell-groups');
    if (!cell) return;
    e.stopPropagation();
    e.preventDefault();
  };

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    document.removeEventListener('mousedown', this.onGroupsMouseDown, true);
    document.removeEventListener('mousemove', this.onGroupsMouseMove, true);
    document.removeEventListener('mouseup', this.onGroupsMouseUp, true);
    document.removeEventListener('click', this.onGroupsClickCapture, true);
  }
}
